import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { listingsApi, chatApi, favouritesApi, formatApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, User, Loader2, ImageOff, MessageCircle, MapPin, ChevronLeft, ChevronRight, Expand, Heart, CheckCircle2, RotateCcw, Edit, Eye } from 'lucide-react';
import { ImageLightbox } from '../components/ImageLightbox';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const categoryColors = {
  electronics: 'bg-primary/10 text-primary',
  vehicles: 'bg-primary/10 text-primary',
  fashion: 'bg-primary/10 text-primary',
  housing: 'bg-primary/10 text-primary',
  furniture: 'bg-primary/10 text-primary',
  beauty: 'bg-primary/10 text-primary',
  food: 'bg-primary/10 text-primary',
  events: 'bg-primary/10 text-primary',
  jobs: 'bg-primary/10 text-primary',
  services: 'bg-primary/10 text-primary',
  community: 'bg-primary/10 text-primary',
  items: 'bg-primary/10 text-primary'
};

const categoryLabels = {
  electronics: 'Electronics',
  vehicles: 'Cars & Vehicles',
  fashion: 'Fashion & Clothing',
  housing: 'Housing',
  furniture: 'Home & Furniture',
  beauty: 'Beauty & Hair',
  food: 'Food & Restaurants',
  events: 'Events',
  jobs: 'Jobs',
  services: 'Services',
  community: 'Community',
  items: 'Items for Sale'
};

const cityLabels = {
  toronto: 'Toronto, ON',
  calgary: 'Calgary, AB',
  edmonton: 'Edmonton, AB',
  vancouver: 'Vancouver, BC',
  ottawa: 'Ottawa, ON',
  winnipeg: 'Winnipeg, MB',
  montreal: 'Montreal, QC',
  mississauga: 'Mississauga, ON',
  brampton: 'Brampton, ON',
  hamilton: 'Hamilton, ON',
  surrey: 'Surrey, BC',
  london: 'London, ON',
  kitchener: 'Kitchener, ON',
  halifax: 'Halifax, NS',
  regina: 'Regina, SK',
  saskatoon: 'Saskatoon, SK'
};

function resolveImageUrl(image) {
  if (!image) return null;
  if (image.startsWith('data:')) return image; // legacy base64
  if (image.startsWith('/api/')) return `${API_URL}${image}`;
  if (image.startsWith('http')) return image;
  return image;
}

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messagingLoading, setMessagingLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isFavourite, setIsFavourite] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const [soldLoading, setSoldLoading] = useState(false);

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const { data } = await listingsApi.getOne(id);
        setListing(data);
      } catch (err) {
        setError('Listing not found');
      } finally {
        setLoading(false);
      }
    };
    fetchListing();
    
    // Check if favourited
    if (isAuthenticated) {
      favouritesApi.getIds().then(({ data }) => {
        setIsFavourite(data.includes(id));
      }).catch(() => {});
    }
  }, [id, isAuthenticated]);

  const handleMessageSeller = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to message the seller');
      navigate('/login', { state: { from: { pathname: `/listing/${id}` } } });
      return;
    }

    // Check if user is the seller
    if (listing.user_id === user._id || listing.user_id === user.id) {
      toast.error('You cannot message yourself');
      return;
    }

    setMessagingLoading(true);
    try {
      const { data } = await chatApi.createConversation(id);
      navigate(`/messages/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setMessagingLoading(false);
    }
  };

  const handleToggleFavourite = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to save listings');
      navigate('/login');
      return;
    }
    setFavLoading(true);
    try {
      if (isFavourite) {
        await favouritesApi.remove(id);
        setIsFavourite(false);
        toast.success('Removed from saved listings');
      } else {
        await favouritesApi.add(id);
        setIsFavourite(true);
        toast.success('Saved to favourites!');
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFavLoading(false);
    }
  };

  const handleToggleSold = async () => {
    setSoldLoading(true);
    try {
      const { data } = await listingsApi.toggleSold(id);
      setListing(prev => ({ ...prev, status: data.status }));
      toast.success(data.status === 'sold' ? 'Listing marked as sold' : 'Listing reactivated');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSoldLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    if (!price) return null;
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const isOwnListing = isAuthenticated && listing && 
    (listing.user_id === user?._id || listing.user_id === user?.id);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="listing-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="listing-error">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">{error || 'Listing not found'}</p>
          <Button onClick={() => navigate('/browse')}>
            Browse Listings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="listing-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate('/');
            }
          }}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column - Image & Description */}
          <div className="lg:col-span-8 space-y-6">
            {/* Image Gallery */}
            {listing.images && listing.images.length > 0 ? (
              <div className="space-y-3">
                {/* Main Image */}
                <div
                  className="aspect-video bg-muted rounded-2xl overflow-hidden relative group cursor-pointer"
                  onClick={() => setLightboxOpen(true)}
                  data-testid="image-gallery-main"
                >
                  <img 
                    src={resolveImageUrl(listing.images[selectedImageIdx])} 
                    alt={listing.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display='none'; e.target.parentElement.classList.add('flex','items-center','justify-center'); }}
                    data-testid="listing-image"
                  />
                  {/* Expand icon */}
                  <div className="absolute top-3 left-3 p-2 bg-background/70 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <Expand className="h-4 w-4" />
                  </div>
                  {listing.images.length > 1 && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedImageIdx(i => (i - 1 + listing.images.length) % listing.images.length); }}
                        className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-background/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background"
                        data-testid="prev-image-btn"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setSelectedImageIdx(i => (i + 1) % listing.images.length); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-background/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-background"
                        data-testid="next-image-btn"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                      <div className="absolute bottom-3 right-3 bg-background/80 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs font-medium">
                        {selectedImageIdx + 1} / {listing.images.length}
                      </div>
                    </>
                  )}
                </div>
                {/* Thumbnails */}
                {listing.images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-1" data-testid="image-thumbnails">
                    {listing.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImageIdx(idx)}
                        className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border-2 transition-all ${
                          idx === selectedImageIdx ? 'border-primary ring-1 ring-primary' : 'border-transparent opacity-70 hover:opacity-100'
                        }`}
                        data-testid={`thumbnail-${idx}`}
                      >
                        <img src={resolveImageUrl(img)} alt={`${idx + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}

                {/* Fullscreen Lightbox */}
                {lightboxOpen && (
                  <ImageLightbox
                    images={listing.images.map(resolveImageUrl)}
                    initialIndex={selectedImageIdx}
                    onClose={() => setLightboxOpen(false)}
                  />
                )}
              </div>
            ) : (
              <div className="aspect-video bg-muted rounded-2xl overflow-hidden">
                <div className="w-full h-full flex items-center justify-center">
                  <ImageOff className="h-16 w-16 text-muted-foreground/40" />
                </div>
              </div>
            )}

            {/* Title & Category */}
            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <h1 
                  className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                  data-testid="listing-title"
                >
                  {listing.title}
                </h1>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {listing.status === 'sold' && (
                    <Badge className="bg-red-600 text-white hover:bg-red-600" data-testid="detail-sold-badge">SOLD</Badge>
                  )}
                  <Badge className={categoryColors[listing.category] || 'bg-gray-100 text-gray-700'}>
                    {categoryLabels[listing.category] || listing.category}
                  </Badge>
                </div>
              </div>
              
              {listing.price && (
                <p className="text-3xl sm:text-4xl font-extrabold text-primary mb-4" data-testid="listing-price">
                  {formatPrice(listing.price)}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Posted {formatDate(listing.created_at)}
                </span>
                {(listing.city || listing.country) && (
                  <span className="flex items-center gap-1" data-testid="listing-location">
                    <MapPin className="h-4 w-4" />
                    {listing.city ? (cityLabels[listing.city] || listing.city) : ''}{listing.city && listing.country ? ', ' : ''}{listing.country || 'Canada'}
                  </span>
                )}
                <span className="flex items-center gap-1" data-testid="listing-views">
                  <Eye className="h-4 w-4" />
                  {listing.views || 0} views
                </span>
              </div>
            </div>

            {/* Description */}
            <div>
              <h2 
                className="text-xl font-bold mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Description
              </h2>
              <p 
                className="text-[15px] text-muted-foreground whitespace-pre-wrap leading-[1.7]"
                data-testid="listing-description"
              >
                {listing.description}
              </p>
            </div>
          </div>

          {/* Right Column - Seller Info */}
          <div className="lg:col-span-4">
            <Card className="sticky top-24">
              <CardContent className="p-6 space-y-6">
                <h3 
                  className="text-lg font-semibold"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Seller Information
                </h3>

                {/* Seller Name */}
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium" data-testid="seller-name">
                      {listing.user_name || 'Anonymous'}
                    </p>
                    <p className="text-sm text-muted-foreground">Member</p>
                  </div>
                </div>

                {/* Message Button */}
                <div className="space-y-3">
                  {isOwnListing ? (
                    <>
                      <Button
                        variant={listing.status === 'sold' ? 'outline' : 'default'}
                        className={`w-full gap-2 ${listing.status === 'sold' ? 'border-primary text-primary' : 'bg-orange-500 hover:bg-orange-600'}`}
                        size="lg"
                        onClick={handleToggleSold}
                        disabled={soldLoading}
                        data-testid="toggle-sold-btn"
                      >
                        {soldLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : listing.status === 'sold' ? (
                          <><RotateCcw className="h-4 w-4" /> Reactivate Listing</>
                        ) : (
                          <><CheckCircle2 className="h-4 w-4" /> Mark as Sold</>
                        )}
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full gap-2" 
                        size="lg"
                        onClick={() => navigate(`/edit-listing/${id}`)}
                        data-testid="edit-listing-btn"
                      >
                        <Edit className="h-4 w-4" />
                        Edit Listing
                      </Button>
                    </>
                  ) : listing.status === 'sold' ? (
                    <Button 
                      className="w-full gap-2 bg-gray-400 hover:bg-gray-400 cursor-not-allowed" 
                      size="lg"
                      disabled
                      data-testid="sold-disabled-btn"
                    >
                      This item has been sold
                    </Button>
                  ) : (
                    <Button 
                      className="w-full gap-2" 
                      size="lg"
                      onClick={handleMessageSeller}
                      disabled={messagingLoading}
                      data-testid="message-seller-btn"
                    >
                      {messagingLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <MessageCircle className="h-4 w-4" />
                      )}
                      Message Seller
                    </Button>
                  )}

                  {/* Save / Favourite Button */}
                  <Button
                    variant="outline"
                    className={`w-full gap-2 ${isFavourite ? 'text-[#E53935] border-[#E53935]/30 bg-red-50 hover:bg-red-100' : ''}`}
                    size="lg"
                    onClick={handleToggleFavourite}
                    disabled={favLoading}
                    data-testid="favourite-btn"
                  >
                    {favLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Heart className={`h-4 w-4 ${isFavourite ? 'fill-[#E53935]' : ''}`} />
                    )}
                    {isFavourite ? 'Saved' : 'Save Listing'}
                  </Button>
                </div>

                {/* Safety Tips */}
                <div className="pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    <strong>Safety Tips:</strong> Meet in a public place, verify the item before payment, and trust your instincts.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
